import {Suspense, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import {generateUUID} from '@di-shared/utils/uuid';
import {diApi} from '@di-shared/api/di';
import {emitEventLog} from '@di-shared/features/analytics';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import {CLIENT_ERROR_CODE} from '@di-shared/features/common/useApiError';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useWindowFocusTracker from '@di-shared/features/common/useWindowFocusTracker';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import LinkTo from '@di-shared/features/navigate/LinkTo';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import {ClickAwayListener} from '@mui/base';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface HighwayUnpaidContentsProps {
  isAgree: boolean;
  vin: string;
}

export enum PaymentExpressPayStatus {
  SUCCESS = 'SUCCESS',
  CANCEL = 'CANCEL',
  FAILURE = 'FAILURE',
}

function HighwayUnpaidContents(props: HighwayUnpaidContentsProps) {
  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense fallback={<HighwayUnpaidContentsLoading />}>
        {props.isAgree ? <AgreeContents {...props} /> : <DisagreeContents {...props} />}
      </Suspense>
    </ErrorBoundary>
  );
}

function AgreeContents({vin}: HighwayUnpaidContentsProps) {
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const puid = useEnvironmentStore((state) => state.puid);
  const {openConfirm, openAlert} = useCcWebviewUtilsHelper();

  const paymentIdRef = useRef(generateUUID());
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPayLoading, setIsPayLoading] = useState(false);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);

  const {data: highwayUnpaidData, refetch: refetchHighwayUnpaid} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.highwayUnpaid.getHighwayUnpaid), puid],
    queryFn: () => diApi.highwayUnpaid.getHighwayUnpaid({puid, vin}),
    select: ({data}) => data,
  });

  const {enableWindowFocusTracker, disableWindowFocusTracker} = useWindowFocusTracker({
    isInitiallyEnabled: false,
    onFocus: async () => {
      await paymentCallback();
    },
  });

  async function paymentCallback() {
    async function waitAndCheckPayment(): Promise<void> {
      // 결제 후 success redirect url로 이동되었는지 확인
      const paymentStatus = window.localStorage.getItem(`express-pay-${paymentIdRef.current}`) as unknown as PaymentExpressPayStatus;

      // CASE 1: 정상 결제
      if (paymentStatus === PaymentExpressPayStatus.SUCCESS) {
        await openAlert({
          title: t('T20372'),
        });
      }
      // CASE 2: 결제 완료 페이지는 넘어갔지만 실제 납부가 완료되지 않음
      else if (paymentStatus === PaymentExpressPayStatus.CANCEL) {
        await openAlert({
          title: t('T20373'),
        });
      }
      // CASE 3: 그 외 에러 상황
      else {
        await openAlert({
          title: t('T20374'),
        });
      }
    }

    await waitAndCheckPayment();
    setIsPayLoading(false);
    disableWindowFocusTracker();
    refetchHighwayUnpaid();
  }

  const [payHighwayUnpaidFee] = usePendingHandler(
    async (licensePlateNumber: string) => {
      enableWindowFocusTracker();
      paymentIdRef.current = generateUUID();

      const response = await diApi.highwayUnpaid.payUnpaidFee({
        puid,
        licensePlateNumber,
        requestBody: {
          approvalUrl: `${document.location.origin}${ROUTE_PATHS.EXPRESS_PAY_PAYMENT_CALLBACK.getFullPath({
            status: PaymentExpressPayStatus.SUCCESS,
            paymentId: paymentIdRef.current,
          })}`,
          cancelUrl: `${document.location.origin}${ROUTE_PATHS.EXPRESS_PAY_PAYMENT_CALLBACK.getFullPath({
            status: PaymentExpressPayStatus.CANCEL,
            paymentId: paymentIdRef.current,
          })}`,
          failureUrl: `${document.location.origin}${ROUTE_PATHS.EXPRESS_PAY_PAYMENT_CALLBACK.getFullPath({
            status: PaymentExpressPayStatus.FAILURE,
            paymentId: paymentIdRef.current,
          })}`,
        },
      });

      WebviewUtils.openNewWebView({
        url: response.data.paymentUrl,
        isModal: true,
        isNavigationBar: false,
        isToolBar: true,
      });

      setIsPayLoading(true);
    },

    async (error: any) => {
      await openAlert({
        title: t('T20374'),
        message: `error [${error?.code || error?.resultCode || CLIENT_ERROR_CODE}]`,
      });
    }
  );

  async function deleteVehicle() {
    if (!highwayUnpaidData.unpaidInfoList[activeSlideIndex]) {
      return;
    }
    const isConfirm = await openConfirm({
      title: t('T20375'),
      message: t('T20376'),
      okText: t('T30544'),
      cancelText: t('T10233'),
    });
    if (isConfirm) {
      await diApi.highwayUnpaid.deleteVehicle({
        puid,
        licensePlateNumber: highwayUnpaidData.unpaidInfoList[activeSlideIndex].licensePlateNumber,
      });
      refetchHighwayUnpaid();
    }
  }

  function goToEditSearchPage(vin: string, licensePlateNumber: string) {
    navigate(ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH_$LICENSE_PLATE_NUMBER.getFullPath(vin, licensePlateNumber));
  }

  async function goToSearchPage(vin: string) {
    if (highwayUnpaidData.unpaidInfoList.length >= 10) {
      await openAlert({
        title: t('T20377'),
      });
      return;
    }
    navigate(ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH.getFullPath(vin));
  }

  if (isPayLoading) {
    const portalRoot = document.getElementById('root');

    if (portalRoot) {
      return ReactDOM.createPortal(
        <div className="popup_wrap">
          <div className="popup loading_popup">
            <div className="popup_inner">
              <div className="popup_contents">
                <div className="loading_box">
                  <div className="loading_lottie">
                    <LottieSelector
                      id="img_json_loading_black_60f"
                      kiaLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                      kiaDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                      hyundaiLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                      hyundaiDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                      genesisLightSrc={() => import('@di-shared/assets/img/json/loading_black_60f.json')}
                      genesisDarkSrc={() => import('@di-shared/assets/img/json/loading_white_60f.json')}
                      loop={true}
                      autoplay={true}
                      style={{width: 25, height: 25}}
                    />
                  </div>
                  <span
                    className="loading_txt"
                    style={{whiteSpace: 'pre-wrap'}}
                  >
                    {t('T20411')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        portalRoot
      );
    }
  }

  if (highwayUnpaidData.unpaidInfoList.length === 0) {
    return <NoRegisteredVehicle vin={vin} />;
  }

  return (
    <li className="application_item asset_after">
      <div className="title_area">
        <strong className="title">{t('T20378')}</strong>
        <button
          type="button"
          className="btn_view"
          onClick={() => setIsMenuOpen(true)}
        >
          <span className="ic ic_more_vertical_16px"></span>
        </button>
        {isMenuOpen && (
          <ClickAwayListener onClickAway={() => setIsMenuOpen(false)}>
            <div className="vehicle_floating_button_area">
              <button
                className="vehicle_btn_item"
                onClick={() => goToSearchPage(vin)}
              >
                <span className="vehicle_btn_text">{t('T20379')}</span>
              </button>
              <button
                className="vehicle_btn_item"
                disabled={highwayUnpaidData.unpaidInfoList.length <= 1}
                onClick={() => deleteVehicle()}
              >
                <span className="vehicle_btn_text">{t('T20380')}</span>
              </button>
            </div>
          </ClickAwayListener>
        )}
      </div>

      <Swiper
        slidesPerView="auto"
        modules={[Pagination]}
        className="application_swiper swiper-autoheight"
        observer
        observeParents
        pagination={{el: '.swiper-pagination', type: 'bullets'}}
        onSlideChange={(swiper) => setActiveSlideIndex(swiper.activeIndex)}
        autoHeight={true}
      >
        {highwayUnpaidData.unpaidInfoList.map((unpaidInfoItem) =>
          unpaidInfoItem.isUnpaid ? (
            <SwiperSlide
              className="application_slide"
              key={unpaidInfoItem.licensePlateNumber}
            >
              <dl className="vehicle_info">
                <div className="vehicle_item">
                  <dt className="info_title">{t('T30520')}</dt>
                  <dd className="info_text">{unpaidInfoItem.licensePlateNumber}</dd>
                </div>

                <div className="vehicle_item">
                  <dt className="info_title">{t('T20383')}</dt>
                  <dd className="info_text">{t('T20384_$amount', {amount: formatNumberWithCommas(unpaidInfoItem.unpaidAmount)})}</dd>
                </div>
              </dl>

              <div className="btn_box">
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  onClick={() => {
                    emitEventLog({
                      event: 'public_service_interaction',
                      data_action: 'public service button click',
                      data_name: '납부하기',
                    });
                    payHighwayUnpaidFee(unpaidInfoItem.licensePlateNumber);
                  }}
                >
                  {t('T20385')}
                </button>
              </div>
            </SwiperSlide>
          ) : (
            <SwiperSlide
              className="application_slide"
              key={unpaidInfoItem.licensePlateNumber}
            >
              <dl className="vehicle_info">
                <div className="vehicle_item">
                  <dt className="info_title">{t('T30520')}</dt>
                  <dd className="info_text">{unpaidInfoItem.licensePlateNumber}</dd>
                </div>
              </dl>
              <div className="guide_box">
                <p
                  className="desc"
                  style={{fontWeight: 'bold'}}
                >
                  {t('T20412')}
                </p>
                <p className="desc">{t('T20381')}</p>
                <p className="desc">{t('T20413')}</p>
              </div>
              <div className="btn_box">
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  onClick={() => {
                    emitEventLog({
                      event: 'public_service_interaction',
                      data_action: 'public service button click',
                      data_name: '차량번호 변경',
                    });
                    goToEditSearchPage(vin, unpaidInfoItem.licensePlateNumber);
                  }}
                >
                  {t('T20382')}
                </button>
              </div>
            </SwiperSlide>
          )
        )}

        {highwayUnpaidData.unpaidInfoList.length > 1 && <div className="swiper-pagination"></div>}
      </Swiper>
    </li>
  );
}

function DisagreeContents({vin}: HighwayUnpaidContentsProps) {
  const {t} = useInferableTranslation();

  return (
    <li className="application_item">
      <LinkTo
        to={ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_ABOUT.getFullPath(vin)}
        className="link"
        onClickBypass={() => {
          emitEventLog({
            event: 'public_service_interaction',
            data_action: 'public service button click',
            data_name: '고속도로 미납통행료 조회·납부',
          });
        }}
      >
        <div className="application_text">
          <strong className="title">{t('T20378')}</strong>
          <p className="desc">{t('T20386')}</p>
        </div>
        <span className="ic ic_arrow_forward_16px"></span>
      </LinkTo>
    </li>
  );
}

function NoRegisteredVehicle({vin}: {vin: string}) {
  const {t} = useInferableTranslation();
  const naviage = useNavigate();

  return (
    <li className="application_item asset_after">
      <div className="application_text">
        <strong className="title">{t('T20378')}</strong>
        <p className="desc">{t('T30557')}</p>
        <div className="btn_box">
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            onClick={() => {
              emitEventLog({
                event: 'public_service_interaction',
                data_action: 'public service button click',
                data_name: '차량 등록하기',
              });
              naviage(ROUTE_PATHS.PUBLIC_SERVICE_HIGHWAY_UNPAID_SEARCH.getFullPath(vin));
            }}
          >
            {t('T30558')}
          </button>
        </div>
      </div>
    </li>
  );
}

function HighwayUnpaidContentsLoading() {
  return (
    <li className="application_item asset_after skeleton">
      <div className="link">
        <div className="application_text">
          <strong className="title">::</strong>
          <p className="desc">::</p>
        </div>
        <span className="ic ic_arrow_forward_16px"></span>
      </div>
    </li>
  );
}

export default HighwayUnpaidContents;
